/* eslint-disable linebreak-style */
/* eslint-disable react/display-name */
import React from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { useSelector } from 'react-redux';
import GridPostsWidgetLoading from 'components/GridPostsWidget/Loading';
import FeaturedPostsWidgetLoading from 'components/FeaturedPostsWidget/Loading';
import MoreTopicsWidgetLoading from 'components/MoreTopicsWidget/Loading';
import RecommendContentWidgetLoading from 'components/RecommendContentWidget/Loading';
import MostSearchedLoading from 'components/MostSearched/Loading';
import CoursesWidgetLoading from 'components/CoursesWidget/Loading';
import CoursesGalleryLoading from 'components/CoursesGallery/Loading';
import DiseaseListLoading from 'components/DiseaseList/Loading';

const AdManager = dynamic(() => import('components/AdManager'));
const TaboolaWidget = dynamic(() => import('components/TaboolaWidget'));
const GridPostsWidget = dynamic(
  () => import('components/GridPostsWidget'),
  { loading: () => (
    <GridPostsWidgetLoading
      mobile={{ width: '480', height: '555' }}
      desktop={{ width: '980', height: '585' }}
    />
  ) });
const FeaturedPostsWidget = dynamic(
  () => import('components/FeaturedPostsWidget'),
  { loading: () => (
    <FeaturedPostsWidgetLoading
      mobile={{ width: '480', height: '550' }}
      desktop={{ width: '350', height: '550' }}
    />
  ) });
const MoreTopicsWidget = dynamic(
  () => import('components/MoreTopicsWidget'),
  { loading: () => (
    <MoreTopicsWidgetLoading
      mobile={{ width: '480', height: '190' }}
      desktop={{ width: '980', height: '310' }}
    />
  ) });
const RecommendContentWidget = dynamic(
  () => import('components/RecommendContentWidget'),
  { loading: () => (
    <RecommendContentWidgetLoading
      mobile={{ width: '480', height: '400' }}
      desktop={{ width: '650', height: '350' }}
    />
  ) });
const MostSearched = dynamic(
  () => import('components/MostSearched'),
  { loading: () => (
    <MostSearchedLoading
      mobile={{ width: '380', height: '100' }}
      desktop={{ width: '980', height: '150' }}
    />
  ) });
const CoursesWidget = dynamic(
  () => import('components/CoursesWidget'),
  { loading: () => (
    <CoursesWidgetLoading
      mobile={{ width: '480', height: '390' }}
      desktop={{ width: '650', height: '390' }}
    />
  ) });
const CoursesGallery = dynamic(
  () => import('components/CoursesGallery'),
  { loading: () => (
    <CoursesGalleryLoading
      mobile={{ width: '480', height: '400' }}
      desktop={{ width: '650', height: '350' }}
    />
  ) });
const DiseaseList = dynamic(
  () => import('components/DiseaseList'),
  { loading: () => (
    <DiseaseListLoading
      mobile={{ width: '480', height: '400' }}
      desktop={{ width: '650', height: '350' }}
    />
  ) });

const Sidebar = ({ sidebarSlug, preview, ads }) => {
  const { items: sidebars } = useSelector((state) => state.sidebars);

  return (
    <React.Fragment>
      {sidebars[sidebarSlug]?.widgets?.map((widget) => {
        switch (widget.type) {
          case 'mc-widget-featured-posts':
            return <FeaturedPostsWidget key={widget.id} widget={widget} />;
          case 'mc-widget-recommend-content':
            return <RecommendContentWidget key={widget.id} widget={widget} />;
          case 'mc-widget-grid':
            return <GridPostsWidget key={widget.id} widget={widget} />;
          case 'mc-widget-more-topics':
            return <MoreTopicsWidget key={widget.id} widget={widget} />;
          case 'mc-widget-most-searched':
            return <MostSearched key={widget.id} widget={widget} />;
          case 'mc-dynamic-ads':
            return !preview && ads?.enabled && (
              <AdManager
                key={widget.id}
                containerId={`ad-slot-${widget.id}`}
                // eslint-disable-next-line camelcase
                type={widget.options?.slot_type}
                targets={ads?.targets}
                display={{
                  desktop: widget.options?.device?.desktop === 'on',
                  tablet: widget.options?.device?.tablet === 'on',
                  mobile: widget.options?.device?.mobile === 'on'
                }}
                keepSizeOnRefresh
              />
            );
          case 'mc-widget-taboola':
            return <TaboolaWidget key={widget.id} widget={widget} />;
          case 'mc-courses-widget':
            return <CoursesWidget key={widget.id} widget={widget} />;
          case 'mc-courses-gallery':
            return <CoursesGallery key={widget.id} widget={widget} />;
          case 'mcs-widget-diseases-list':
            return <DiseaseList key={widget.id} widget={widget} />;
          default:
            return null;
        }
      })}
    </React.Fragment>
  );
};

Sidebar.defaultProps = {
  preview: false,
  ads: {
    enabled: true,
    targets: {}
  },
};

Sidebar.propTypes = {
  sidebarSlug: PropTypes.string.isRequired,
  preview: PropTypes.bool,
  ads: PropTypes.shape({
    enabled: PropTypes.bool,
    targets: PropTypes.object,
  })
};

export default Sidebar;
